import { isBefore } from 'date-fns';
import * as Yup from 'yup';
import { asSelectOptions } from '../../../../select-options/utils/asSelectOptions';
import {
  energyClassLabels, jobTitleLabels, ownershipTypeLabels, propertyStatusLabels, propertyTypeLabels,
} from '../../../../labels';
import { registryCategoryLabels } from '../../../../labels/registryCategoryLabels';
import { heatingTypeLabels } from '../../../../property/labels';
import { formatInputDate } from '../../../../utils/form';
import { TemplateFieldConfig } from '../../../types';
import type { BrandTemplateAutofillAgentData } from '../../../utils/autofill/types';
import { Agency, CoveredGeo } from '../../../../providers/api/dtos';

function timeToMinutes(time: string) {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export const PROPERTY_DATA_TEMPLATE_FIELD_CONFIG: TemplateFieldConfig[] = [
  {
    name: 'property_data_description',
    inputType: 'text-area',
    fieldName: 'description',
    label: 'Descrizione',
    gridUnit: 1,
    inputProps: {
      rows: 8,
    },
  },
  {
    name: 'property_data_abstract',
    inputType: 'text-area',
    fieldName: 'abstract',
    label: 'Abstract',
    gridUnit: 1,
    inputProps: {
      rows: 5,
    },
  },
  {
    name: 'property_data_composition',
    inputType: 'text-area',
    fieldName: 'composition',
    label: 'Composizione',
    gridUnit: 1,
    inputProps: {
      rows: 5,
    },
  },
  {
    name: 'property_data_status',
    inputType: 'select',
    fieldName: 'propertyStatus',
    label: 'Stato',
    gridUnit: 1 / 2,
    inputProps: {
      options: asSelectOptions(propertyStatusLabels),
    },
  },
  {
    name: 'property_data_type',
    inputType: 'select',
    fieldName: 'propertyType',
    label: 'Tipologia',
    gridUnit: 1 / 2,
    inputProps: {
      options: asSelectOptions(propertyTypeLabels),
    },
  },
  {
    name: 'property_data_registryCategory',
    inputType: 'select',
    fieldName: 'registryCategory',
    label: 'Categoria catastale',
    gridUnit: 1 / 2,
    inputProps: {
      options: asSelectOptions(registryCategoryLabels),
    },
  },
  {
    name: 'property_data_suburb',
    inputType: 'text-input',
    fieldName: 'geo.suburb',
    label: 'Quartiere',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_additionalExpensesFormatted',
    inputType: 'currency-input',
    fieldName: 'additionalExpenses',
    label: 'Spese condominiali mensili',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_heating',
    inputType: 'select',
    fieldName: 'heating.type',
    label: 'Riscaldamento',
    gridUnit: 1 / 2,
    inputProps: {
      options: asSelectOptions(heatingTypeLabels),
    },
  },
  {
    name: 'property_data_energyValueFormatted',
    inputType: 'number-input',
    fieldName: 'energyValue',
    label: 'Indice di prestazione energetica (kW h/m² anno)',
    gridUnit: 1 / 2,
    inputProps: {
      min: 0,
      step: 'any',
    },
  },
  {
    name: 'property_data_ownershipType',
    inputType: 'select',
    fieldName: 'ownershipType',
    label: 'Tipologia transazione',
    gridUnit: 1 / 2,
    inputProps: {
      options: asSelectOptions(ownershipTypeLabels),
    },
  },
  {
    name: 'property_data_energyClass',
    inputType: 'select',
    fieldName: 'energyClass',
    label: 'Classe energetica',
    gridUnit: 1 / 2,
    inputProps: {
      options: asSelectOptions(energyClassLabels),
    },
  },
  {
    name: 'property_data_size',
    inputType: 'size-input',
    fieldName: 'propertySize',
    label: 'Superficie',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_sizeFormatted',
    inputType: 'size-input',
    fieldName: 'propertySize',
    label: 'Superficie',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_floor',
    inputType: 'number-input',
    fieldName: 'mainFloor',
    label: 'Piano principale',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_floorShort',
    inputType: 'number-input',
    fieldName: 'mainFloor',
    label: 'Piano principale',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_buildingFloors',
    inputType: 'number-input',
    fieldName: 'buildingFloors',
    label: 'Piani totali',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_roomsNumber',
    inputType: 'number-input',
    fieldName: 'roomsNumber',
    label: 'Locali',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_price',
    inputType: 'currency-input',
    fieldName: 'price',
    label: 'Prezzo',
    gridUnit: 1 / 2,
    validation: Yup.number().test('isValid', 'Il prezzo deve essere positivo.', (value) => value === undefined || value > 0),
  },
  {
    name: 'property_data_priceFormatted',
    inputType: 'currency-input',
    fieldName: 'price',
    label: 'Prezzo',
    gridUnit: 1 / 2,
    validation: Yup.number().test('isValid', 'Il prezzo deve essere positivo.', (value) => value === undefined || value > 0),
  },
  {
    name: 'property_data_soldPrice',
    inputType: 'currency-input',
    fieldName: 'soldPrice',
    label: 'Prezzo di vendita',
    gridUnit: 1 / 2,
    validation: Yup.number().test('isValid', 'Il prezzo di vendita deve essere positivo.', (value) => value === undefined || value > 0),
  },
  {
    name: 'property_data_soldPriceFormatted',
    inputType: 'currency-input',
    fieldName: 'soldPrice',
    label: 'Prezzo di vendita',
    gridUnit: 1 / 2,
    validation: Yup.number().test('isValid', 'Il prezzo di vendita deve essere positivo.', (value) => value === undefined || value > 0),
  },
  {
    name: 'property_data_moneySaved',
    inputType: 'currency-input',
    fieldName: 'moneySaved',
    label: 'Risparmio economico',
    gridUnit: 1 / 2,
    validation: Yup.number().test('isValid', 'Il risparmio economico deve essere positivo.', (value) => value === undefined || value > 0),
  },
  {
    name: 'property_data_moneySavedFormatted',
    inputType: 'currency-input',
    fieldName: 'moneySaved',
    label: 'Risparmio economico',
    gridUnit: 1 / 2,
    validation: Yup.number().test('isValid', 'Il risparmio economico deve essere positivo.', (value) => value === undefined || value > 0),
  },
  {
    name: 'property_data_salesTime',
    inputType: 'time-unit-input',
    fieldName: 'soldData',
    label: 'Tempo della vendita',
    gridUnit: 1 / 2,
    inputProps: {
      options: [
        { label: 'Giorni', value: 'DAYS' },
        { label: 'Settimane', value: 'WEEKS' },
      ],
    },
  },
  {
    name: 'property_data_salesTimeLabel',
    inputType: 'time-unit-input',
    fieldName: 'soldData',
    label: 'Tempo della vendita',
    gridUnit: 1 / 2,
    inputProps: {
      options: [
        { label: 'Giorni', value: 'DAYS' },
        { label: 'Settimane', value: 'WEEKS' },
      ],
    },
  },
  {
    name: 'property_data_openHouseDate',
    inputType: 'date-picker',
    fieldName: 'openHouseDate',
    label: 'Data Open House',
    gridUnit: 1 / 2,
    validation: Yup.string().test('isValid', 'La data non può essere nel passato.', (value) => {
      if (value && isBefore(new Date(value), new Date())) {
        return false;
      }

      return true;
    }),
    inputProps: {
      min: formatInputDate(Date.now()),
    },
  },
  {
    name: 'property_data_openHouseTime',
    inputType: 'timerange-picker',
    fieldName: 'openHouseTime',
    label: 'Ora Open House (inizio - fine)',
    gridUnit: 1 / 2,
    validation: Yup.string().test('isValid', 'Messaggio', (_, context) => {
      if (
        context.parent.property_data_openHouseTimeStart
        && context.parent.property_data_openHouseTimeEnd
        && timeToMinutes(context.parent.property_data_openHouseTimeStart) >= timeToMinutes(context.parent.property_data_openHouseTimeEnd)
      ) {
        return context.createError({
          message: 'L\'ora di fine dell\'open house deve essere successiva all\'ora di inizio.',
          path: 'property_data_openHouseTimeEnd',
        });
      }

      return true;
    }),
  },
  {
    name: 'property_data_locality',
    inputType: 'text-input',
    fieldName: 'geo.locality',
    label: 'Località',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_province',
    inputType: 'text-input',
    fieldName: 'geo.province',
    label: 'Provincia',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_provinceShort',
    inputType: 'text-input',
    fieldName: 'geo.plateCode',
    label: 'Sigla provincia',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_realAddress',
    inputType: 'text-input',
    fieldName: 'geo.normalizedAddress',
    label: 'Indirizzo (reale)',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_realAddressShort',
    inputType: 'text-input',
    fieldName: 'realAddressShort',
    label: 'Indirizzo abbreviato (reale)',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_publicationAddress',
    inputType: 'text-input',
    fieldName: 'publicationAddress',
    label: 'Indirizzo (pubblicazione)',
    gridUnit: 1 / 2,
  },
  {
    name: 'property_data_publicationAddressShort',
    inputType: 'text-input',
    fieldName: 'publicationAddressShort',
    label: 'Indirizzo Abbreviato (pubblicazione)',
    gridUnit: 1 / 2,
  },
];

export const AGENT_DATA_TEMPLATE_FIELD_CONFIG = (agentData?: BrandTemplateAutofillAgentData): TemplateFieldConfig[] => [
  {
    name: 'agent_data_name',
    inputType: 'text-input',
    fieldName: 'name',
    label: 'Nome',
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_surname',
    inputType: 'text-input',
    fieldName: 'surname',
    label: 'Cognome',
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_fullname',
    inputType: 'text-input',
    fieldName: 'fullName',
    label: 'Nome completo',
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_email',
    inputType: 'text-input',
    fieldName: 'email',
    label: 'Email',
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_mobile',
    inputType: 'text-input',
    fieldName: 'mobile',
    label: 'Telefono',
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_jobTitle',
    inputType: 'select',
    fieldName: 'jobTitle',
    label: 'Ruolo',
    inputProps: {
      options: asSelectOptions(jobTitleLabels),
    },
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_bio',
    inputType: 'text-area',
    fieldName: 'bio',
    label: 'Biografia',
    gridUnit: 1,
    inputProps: {
      rows: 5,
    },
  },
  {
    name: 'agent_data_province',
    inputType: 'autocomplete',
    label: 'Provincia',
    fieldName: 'mostCoveredGeo',
    inputProps: {
      minLength: 1,
      suggestions: agentData?.coveredGeos,
      printSuggestion: (geo: CoveredGeo) => geo.province,
    },
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_agencyAddress',
    inputType: 'autocomplete',
    label: 'Indirizzo agenzia',
    fieldName: 'mainAgency',
    inputProps: {
      minLength: 1,
      suggestions: agentData?.agencies,
      printSuggestion: (agency: Agency) => `${agency.shortName} - ${agency.address.normalizedAddress}`,
    },
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_moneySavedAverage',
    inputType: 'currency-input',
    fieldName: 'moneySavedAvg',
    label: 'Risparmio economico',
    gridUnit: 1 / 2,
    validation: Yup.number().test('isValid', 'Il risparmio economico deve essere positivo.', (value) => value === undefined || value > 0),
  },
  {
    name: 'agent_data_moneySavedAverageFormatted',
    inputType: 'currency-input',
    fieldName: 'moneySavedAvg',
    label: 'Risparmio economico',
    gridUnit: 1 / 2,
    validation: Yup.number().test('isValid', 'Il risparmio economico deve essere positivo.', (value) => value === undefined || value > 0),
  },
  {
    name: 'agent_data_salesAverageTime',
    inputType: 'time-unit-input',
    fieldName: 'salesTimeAvg',
    label: 'Tempo medio vendita',
    validation: Yup.number().test('isValid', 'Il tempo medio di vendita deve essere positivo.', (value) => value === undefined || value > 0),
    gridUnit: 1 / 2,
    inputProps: {
      options: [
        { label: 'Giorni', value: 'DAYS' },
        { label: 'Settimane', value: 'WEEKS' },
      ],
    },
  },
  {
    name: 'agent_data_salesAverageTimeLabel',
    inputType: 'time-unit-input',
    fieldName: 'salesTimeAvg',
    label: 'Tempo medio vendita',
    validation: Yup.number().test('isValid', 'Il tempo medio di vendita deve essere positivo.', (value) => value === undefined || value > 0),
    gridUnit: 1 / 2,
    inputProps: {
      options: [
        { label: 'Giorni', value: 'DAYS' },
        { label: 'Settimane', value: 'WEEKS' },
      ],
    },
  },
];

export const AGENT_DATA_PREVIEWS_TEMPLATE_FIELD_CONFIG: TemplateFieldConfig[] = [
  {
    name: 'agent_data_reviews($POSITION)_author',
    inputType: 'text-input',
    fieldName: 'author',
    label: 'Autore',
    gridUnit: 1 / 2,
  },
  {
    name: 'agent_data_reviews($POSITION)_title',
    inputType: 'text-input',
    fieldName: 'title',
    label: 'Titolo',
    gridUnit: 1,
  },
  {
    name: 'agent_data_reviews($POSITION)_text',
    inputType: 'text-area',
    fieldName: 'text',
    label: 'Testo',
    gridUnit: 1,
    inputProps: {
      rows: 8,
    },
  },
  {
    name: 'agent_data_reviews($POSITION)_date',
    inputType: 'date-picker',
    fieldName: 'date',
    label: 'Data',
    gridUnit: 1 / 2,
  },
];
